import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";import api, { fmtCLP, resolveImg } from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import InvestorActions from "../components/InvestorActions";
import InvestorDocuments from "../components/InvestorDocuments";
import {
  MapPin, Calendar, Users, Clock, TrendingUp, DollarSign, ChevronLeft,
  ChevronRight, Heart, Shield, Key, MessageSquareLock, BadgeCheck, AlertTriangle
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

export default function BusinessDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [biz, setBiz] = useState(null);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get(`/businesses/${slug}`).then((r) => setBiz(r.data)).catch(() => navigate("/marketplace"));
  }, [slug]);

  if (!biz) return (
    <div className="min-h-screen bg-zinc-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-20 animate-pulse">
        <div className="h-96 bg-zinc-200 rounded-lg" />
      </div>
      <Footer />
    </div>
  );

  const images = biz.images || [];
  const current = images[idx];

  return (
    <div className="min-h-screen bg-zinc-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/marketplace" className="text-sm text-slate-500 hover:underline inline-flex items-center gap-1 mb-6" data-testid="back-to-marketplace">
          <ChevronLeft className="w-4 h-4" /> Volver al marketplace
        </Link>

        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="relative aspect-video bg-zinc-200 rounded-xl overflow-hidden border border-zinc-200">
              {current && (
                <img src={resolveImg(current.storage_path)} alt={biz.title} className="w-full h-full object-cover" />
              )}
              <div className="absolute top-4 left-4 flex flex-col gap-2">
                <Badge className="bg-emerald-600 text-white border-0">Negocio funcionando</Badge>
                <Badge className="bg-amber-600 text-white border-0 flex items-center gap-1">
                  <Key className="w-3 h-3" /> Derecho de llave
                </Badge>
              </div>
              {images.length > 1 && (
                <>
                  <button onClick={() => setIdx((idx - 1 + images.length) % images.length)} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center" data-testid="gallery-prev">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button onClick={() => setIdx((idx + 1) % images.length)} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center" data-testid="gallery-next">
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>

            {images.length > 1 && (
              <div className="mt-4 grid grid-cols-5 gap-2">
                {images.map((im, i) => (
                  <button key={im.id} onClick={() => setIdx(i)} className={`aspect-square rounded-md overflow-hidden border-2 ${i === idx ? "border-slate-900" : "border-transparent"}`} data-testid={`gallery-thumb-${i}`}>
                    <img src={resolveImg(im.storage_path)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            <div className="mt-10">
              <span className="overline text-emerald-700">{biz.category_name}</span>
              <h1 className="mt-2 font-display text-3xl lg:text-4xl font-extrabold tracking-tight" data-testid="business-title">{biz.title}</h1>
              <p className="mt-3 flex items-center gap-1.5 text-sm text-slate-600">
                <MapPin className="w-4 h-4" /> {biz.comuna_name}, {biz.region_name}
              </p>

              {(biz.interested_count || 0) > 0 && (
                <div
                  className="mt-4 inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-rose-50 border border-rose-200 text-rose-800"
                  data-testid="detail-interest-badge"
                >
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-rose-500 opacity-60 animate-ping"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-600"></span>
                  </span>
                  <Heart className="w-3.5 h-3.5 fill-rose-600 text-rose-600" />
                  <span className="text-sm font-semibold">
                    {biz.interested_count} {biz.interested_count === 1 ? "inversionista interesado" : "inversionistas interesados"}
                  </span>
                  {biz.interested_count >= 3 && (
                    <span className="text-xs font-medium text-rose-700">· alta demanda</span>
                  )}
                </div>
              )}

              <Tabs defaultValue="overview" className="mt-8">
                <TabsList>
                  <TabsTrigger value="overview" data-testid="tab-overview">Resumen</TabsTrigger>
                  <TabsTrigger value="finances" data-testid="tab-finances">Información financiera</TabsTrigger>
                  <TabsTrigger value="assets" data-testid="tab-assets">Activos incluidos</TabsTrigger>
                  <TabsTrigger value="documents" data-testid="tab-documents">Documentos</TabsTrigger>
                </TabsList>
                <TabsContent value="overview" className="space-y-5 mt-6">
                  <p className="text-slate-700 leading-relaxed whitespace-pre-line">{biz.description}</p>
                  {biz.sale_reason && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <p className="text-xs font-semibold text-amber-800 uppercase tracking-wider">Motivo de venta</p>
                      <p className="mt-1 text-sm text-amber-900">{biz.sale_reason}</p>
                    </div>
                  )}
                  <div className="grid sm:grid-cols-2 gap-4">
                    {biz.years_operating != null && (<MetricCard icon={<Calendar />} label="Años operando" value={biz.years_operating} />)}
                    {biz.employees_count != null && (<MetricCard icon={<Users />} label="Trabajadores" value={biz.employees_count} />)}
                    {biz.operating_hours && (<MetricCard icon={<Clock />} label="Horario" value={biz.operating_hours} />)}
                    {biz.rent_amount != null && biz.rent_amount > 0 && (<MetricCard icon={<DollarSign />} label="Arriendo" value={fmtCLP(biz.rent_amount)} />)}
                    {biz.address_reference && (<MetricCard icon={<MapPin />} label="Sector" value={biz.address_reference} />)}
                  </div>
                  {biz.permits && (
                    <div className="bg-white border border-zinc-200 rounded-lg p-4">
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Permisos y patentes</p>
                      <p className="mt-2 text-sm whitespace-pre-line">{biz.permits}</p>
                    </div>
                  )}
                </TabsContent>
                <TabsContent value="finances" className="mt-6">
                  {biz.monthly_revenue != null || biz.monthly_profit != null ? (
                    <div className="grid sm:grid-cols-3 gap-4">
                      <MetricCard icon={<TrendingUp />} label="Ventas mensuales" value={fmtCLP(biz.monthly_revenue)} accent="emerald" />
                      <MetricCard icon={<DollarSign />} label="Costos mensuales" value={fmtCLP(biz.monthly_costs)} />
                      <MetricCard icon={<TrendingUp />} label="Utilidad mensual" value={fmtCLP(biz.monthly_profit)} accent="emerald" />
                    </div>
                  ) : (
                    <div className="bg-white border border-zinc-200 rounded-lg p-6 text-sm text-slate-500">
                      La información financiera detallada se comparte sólo con inversionistas autorizados tras la solicitud.
                    </div>
                  )}
                </TabsContent>
                <TabsContent value="assets" className="mt-6 space-y-3">
                  {[
                    { l: "Capacitación al nuevo dueño", v: biz.includes_training },
                    { l: "Cartera de proveedores", v: biz.includes_suppliers },
                    { l: "Cartera de clientes", v: biz.includes_clients },
                  ].map((it, i) => (
                    <div key={i} className="flex items-center gap-3 p-4 bg-white border border-zinc-200 rounded-lg">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${it.v ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-400"}`}>
                        {it.v ? <BadgeCheck className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                      </div>
                      <p className="text-sm font-medium">{it.l}: <span className={it.v ? "text-emerald-700" : "text-slate-400"}>{it.v ? "Sí incluido" : "No incluido"}</span></p>
                    </div>
                  ))}
                  {biz.inventory_included && (<div className="bg-white border border-zinc-200 rounded-lg p-4"><p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Inventario incluido</p><p className="mt-2 text-sm">{biz.inventory_included}</p></div>)}
                  {biz.assets_included && (<div className="bg-white border border-zinc-200 rounded-lg p-4"><p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Activos incluidos</p><p className="mt-2 text-sm">{biz.assets_included}</p></div>)}
                </TabsContent>
                <TabsContent value="documents" className="mt-6">
                  <InvestorDocuments businessId={biz.id} />
                </TabsContent>
              </Tabs>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-5">
            <div className="sticky top-24 space-y-6">
              <div className="bg-white border border-zinc-200 rounded-xl p-6">
                <p className="overline text-emerald-700">Precio de venta</p>
                <p className="mt-2 font-display text-4xl lg:text-5xl font-extrabold tracking-tight" data-testid="sale-price">{fmtCLP(biz.sale_price)}</p>
                {biz.monthly_profit != null && (
                  <div className="mt-4 flex items-center gap-2 text-sm text-emerald-700">
                    <TrendingUp className="w-4 h-4" /> Utilidad estimada: <strong>{fmtCLP(biz.monthly_profit)}/mes</strong>
                  </div>
                )}
              </div>

              <InvestorActions business={biz} salePrice={parseFloat(biz.sale_price)} />

              <div className="bg-slate-900 text-slate-100 rounded-xl p-6">
                <div className="flex items-center gap-3">
                  <Shield className="w-6 h-6 text-amber-400 shrink-0" />
                  <h4 className="font-display font-bold text-lg">Confidencialidad protegida</h4>
                </div>
                <ul className="mt-5 space-y-3 text-sm text-slate-300">
                  <li className="flex items-start gap-2"><BadgeCheck className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> El equipo DerechoLlave es tu único interlocutor durante el proceso.</li>
                  <li className="flex items-start gap-2"><BadgeCheck className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> Identidad y datos del vendedor protegidos hasta la visita acompañada.</li>
                  <li className="flex items-start gap-2"><BadgeCheck className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> Documentos privados sólo con autorización del equipo DerechoLlave.</li>
                  <li className="flex items-start gap-2"><MessageSquareLock className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> Chat directo con el equipo, sin filtros: puedes compartir tus datos de contacto.</li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function MetricCard({ icon, label, value, accent }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-4">
      <div className={`w-8 h-8 rounded-md flex items-center justify-center ${accent === "emerald" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700"} mb-2`}>
        {React.cloneElement(icon, { className: "w-4 h-4" })}
      </div>
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`mt-1 font-display font-bold text-lg ${accent === "emerald" ? "text-emerald-700" : "text-slate-900"}`}>{value}</p>
    </div>
  );
}
